/**
 * API 请求客户端 - axios 实例、拦截器、基础方法
 */
import axios, { type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import { ElMessage } from 'element-plus';
import { router } from '@/router';
import { useUserStore } from '@/stores/user';
import {
  BIZ_IM_SIGN_EXPIRED,
  HTTP_FORBIDDEN,
  HTTP_NOT_FOUND,
  HTTP_UNAUTHORIZED,
  HTTP_UNPROCESSABLE_ENTITY,
} from '@/constants/http';
import { toBearerHeader } from '@/utils/token';
import {
  attachApiContext,
  markRequestStart,
  markRequestSuccess,
  reportRequestFailure,
  reportSlowRequest,
  serializePayload,
} from '@/monitor/collectors/apiError';

/**
 * VITE_API_BASE / VITE_BASE_URL：接口根地址，如 https://example.com 或 https://example.com/api
 * 若不含 /api 则自动拼接为 BASE + '/api'
 */
const BASE = (import.meta.env.VITE_API_BASE || import.meta.env.VITE_BASE_URL || '').replace(
  /\/$/,
  '',
);
const API_BASE = BASE ? (BASE.includes('/api') ? BASE : BASE + '/api') : '/api';
/** 公共接口（无需登录态）的根路径，与 API_BASE 共用同一域名 */
const PUBLIC_API_BASE = (BASE ? BASE.replace(/\/api\/?$/, '') : '') + '/public-api';

export const client = axios.create({
  baseURL: API_BASE,
  timeout: Number(import.meta.env.VITE_TIMEOUT ?? 10000),
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

/** 公共接口客户端：不附加 Authorization、不弹 toast、不跳登录，由调用方处理错误 */
export const publicClient = axios.create({
  baseURL: PUBLIC_API_BASE,
  timeout: Number(import.meta.env.VITE_TIMEOUT ?? 10000),
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

publicClient.interceptors.response.use(
  (res: AxiosResponse) => {
    const data = res.data;
    if (data?.code !== 0 && data?.code !== undefined) {
      return Promise.reject(new Error(data.msg || '请求失败'));
    }
    return data?.data;
  },
  (err) => Promise.reject(err),
);

const redirectToLogin = () => {
  /** 异步关停 IM，不阻塞跳转 */
  import('@/stores/im').then(({ useImStore }) =>
    useImStore()
      .shutdown()
      .catch(() => void 0),
  );
  useUserStore().logout();
  const path = window.location.pathname + window.location.search || '/';
  router.replace({ name: 'Login', query: { redirect: path, expired: '1' } }).catch(() => void 0);
};

/** 并发刷签去重：多个请求同时返回 70001 时只发起一次刷新 */
let imSignRefreshing: Promise<void> | null = null;

const refreshAgentImSign = (): Promise<void> => {
  if (imSignRefreshing) return imSignRefreshing;
  imSignRefreshing = (async () => {
    try {
      const [{ queryRefreshImSign }, { useImStore }] = await Promise.all([
        import('@/api/auth'),
        import('@/stores/im'),
      ]);
      const data = await queryRefreshImSign();
      useUserStore().updateImCredentials({
        im_app_id: data.im_app_id,
        im_identifier: data.im_identifier,
        im_user_sign: data.im_user_sign,
      });
      const imStore = useImStore();
      await imStore.shutdown().catch(() => void 0);
      await imStore.bootstrap();
    } catch (e) {
      console.warn('[IM] refresh sign 失败', e);
    } finally {
      imSignRefreshing = null;
    }
  })();
  return imSignRefreshing;
};

client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const auth = toBearerHeader(localStorage.getItem('token'));
    if (auth) config.headers.Authorization = auth;
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    /** 监控：记录请求起始时间，供响应侧计算耗时 */
    markRequestStart(config);
    return config;
  },
  (err) => Promise.reject(err),
);

client.interceptors.response.use(
  (res: AxiosResponse) => {
    const data = res.data;
    const silent = (res.config as { silent?: boolean })?.silent;
    /** 监控：成功即清掉容忍型接口的连续失败计数 */
    markRequestSuccess(res.config);
    /** 监控：成功但耗时超阈值的慢接口 */
    reportSlowRequest(res.config);
    if (data?.code !== 0 && data?.code !== undefined) {
      /**
       * 监控：接口信息在 reject 的那一刻就丢了，若错误没人 catch、
       * 冒到 Vue errorHandler，堆栈里只剩 axios 内部帧。这里先挂上上下文，
       * 让告警能显示接口路径和参数。
       */
      const rejectWithContext = (message: string) => {
        const error = new Error(message);
        attachApiContext(error, {
          method: (res.config?.method ?? 'get').toUpperCase(),
          url: res.config?.url ?? '',
          code: data.code,
          payload: serializePayload(res.config?.data ?? res.config?.params),
        });
        return Promise.reject(error);
      };
      if (data.code === HTTP_UNAUTHORIZED) {
        redirectToLogin();
        return rejectWithContext(data.msg || '请求失败');
      }
      if (data.code === BIZ_IM_SIGN_EXPIRED && !res.config?.url?.includes('refresh-im-sign')) {
        /** IM 签名过期，静默刷新；当前请求依旧返回错误，由调用方决定是否重试 */
        refreshAgentImSign();
        return rejectWithContext(data.msg || 'IM 签名已过期，正在刷新');
      }
      if (!silent) ElMessage.error(data.msg || '请求失败');
      return rejectWithContext(data.msg || '请求失败');
    }
    return data?.data;
  },
  (err) => {
    const e = err as {
      config?: {
        silent?: boolean;
        method?: string;
        url?: string;
        data?: unknown;
        params?: unknown;
      };
      response?: { status?: number; data?: { code?: number; msg?: string } };
    };
    const res = e?.response;
    const status = res?.status;
    const data = res?.data;
    const silent = e?.config?.silent;
    /** 监控：只上报 5xx / 网络错误 / 超时，4xx 与业务错误不报 */
    reportRequestFailure(err);
    /**
     * 监控：挂上接口上下文。若调用方没 catch、错误冒到组件层，
     * 告警才能显示接口路径和参数，而不是一堆 axios 内部堆栈。
     * 不修改 err.message（调用方可能依赖），后端文案单独放进 context.message。
     */
    attachApiContext(err, {
      method: (e?.config?.method ?? 'get').toUpperCase(),
      url: e?.config?.url ?? '',
      status,
      code: data?.code,
      message: data?.msg,
      payload: serializePayload(e?.config?.data ?? e?.config?.params),
    });
    if (status === HTTP_UNAUTHORIZED || data?.code === HTTP_UNAUTHORIZED) redirectToLogin();
    else if (silent) {
      /** 静默请求：失败不弹提示，由调用方处理 */
    } else if (status === HTTP_FORBIDDEN) ElMessage.error('您没有这个权限！');
    else if (status === HTTP_NOT_FOUND) ElMessage.error('找不到相关信息！');
    else if (status === HTTP_UNPROCESSABLE_ENTITY) ElMessage.error(data?.msg || '参数错误');
    else ElMessage.error(data?.msg || '网络异常');
    return Promise.reject(err);
  },
);

/**
 * apiRequest 选项类型
 *
 * 注意：axios 用 `data` 传请求体，**不要**使用 `body`（fetch API 才是 body）。
 * 此处用 `body?: never` 在编译期阻止误用。
 */
export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  timeout?: number;
  /** 静默请求：失败时不弹错误提示（用于后台探测类调用，由调用方自行处理结果） */
  silent?: boolean;
  /**
   * 容忍偶发失败：单次失败不上报监控，连续失败达阈值才报。
   * 用于心跳这类用户无感的后台请求 —— 单次超时多半是客户端休眠或网络瞬断。
   */
  tolerateFailure?: boolean;
  /** @deprecated 请使用 `data` 字段。axios 不识别 `body`，此字段已禁用。 */
  body?: never;
}

export const apiRequest = async <T = unknown>(
  url: string,
  options: ApiRequestOptions = {},
): Promise<T> => {
  return client.request({ url, ...options }) as Promise<T>;
};

export const apiGet = async <T = unknown>(
  url: string,
  params?: Record<string, unknown>,
): Promise<T> => {
  return apiRequest<T>(url, { method: 'GET', params });
};

export const publicApiGet = async <T = unknown>(
  url: string,
  params?: Record<string, unknown>,
): Promise<T> => {
  return publicClient.request({ url, method: 'GET', params }) as Promise<T>;
};

export const apiPost = async <T = unknown>(url: string, data?: unknown): Promise<T> => {
  return apiRequest<T>(url, { method: 'POST', data });
};

export const apiPut = async <T = unknown>(url: string, data?: unknown): Promise<T> => {
  return apiRequest<T>(url, { method: 'PUT', data });
};

/** DELETE 请求 */
export const apiDelete = async <T = unknown>(url: string): Promise<T> => {
  return apiRequest<T>(url, { method: 'DELETE' });
};

/**
 * 解析 Content-Disposition 头中的文件名
 * - 优先使用 RFC 5987 的 filename*=charset''encoded（多为 UTF-8 编码的中文名）
 * - 退回普通 filename=xxx，并跳过 ".xlsx" 这类后端给的近似空名
 */
const parseContentDispositionFilename = (disposition: string | null): string => {
  if (!disposition) return '';
  const star = disposition.match(/filename\*\s*=\s*([^']*)''([^;\n]+)/i);
  if (star?.[2]) {
    const raw = star[2].trim().replace(/^["']|["']$/g, '');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const plain = disposition.match(/filename\s*=\s*["']?([^"';\n]+?)["']?\s*(?:;|$)/i);
  const name = plain?.[1]?.trim() ?? '';
  if (!name || /^\.[A-Za-z0-9]+$/.test(name)) return '';
  return name;
};

export const apiDownload = async (
  url: string,
  options: { filename?: string; method?: string; body?: string } & Record<string, unknown> = {},
) => {
  const { filename: optFilename, ...rest } = options;
  const auth = toBearerHeader(localStorage.getItem('token'));
  const headers: Record<string, string> = {
    ...(rest.headers as Record<string, string>),
    ...(auth && { Authorization: auth }),
    ...(rest.body && { 'Content-Type': 'application/json' }),
  };
  const origin = (BASE || '').replace(/\/api\/?$/, '') || '';
  const res = await fetch(`${origin}/api${url}`, { ...rest, headers } as RequestInit);
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { code?: number; msg?: string };
    if (data?.code === HTTP_UNAUTHORIZED || res.status === HTTP_UNAUTHORIZED) redirectToLogin();
    throw new Error(data?.msg || '下载失败');
  }
  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(res.headers.get('Content-Disposition')) || optFilename || '';
  if (!filename) {
    console.warn(
      `[apiDownload] 未取到文件名：${url}\n` +
        '可能原因：后端未在 Access-Control-Expose-Headers 暴露 Content-Disposition，或未设置该响应头。',
    );
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};
